import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import type { CompletionStep, CompletionStepStatus } from '../state'

function StepIndicator({ status }: { status: CompletionStepStatus }) {
	if (status === 'active') {
		return <div className="size-5 rounded-full border-2 border-grey-200 border-t-brand-500 animate-spin" />
	}

	if (status === 'pending') {
		return <div className="size-5 rounded-full border-2 border-grey-200" />
	}

	if (status === 'error') {
		return (
			<div className="size-5 rounded-full bg-red-500 flex items-center justify-center">
				<svg
					viewBox="0 0 24 24"
					className="size-3 text-white"
					fill="none"
					stroke="currentColor"
					strokeWidth={3}
				>
					<path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
				</svg>
			</div>
		)
	}

	// done or skipped — a check, muted when skipped
	const tone = status === 'skipped' ? 'bg-grey-300' : 'bg-brand-500'
	return (
		<div className={`size-5 rounded-full ${tone} flex items-center justify-center`}>
			<svg viewBox="0 0 24 24" className="size-3 text-white" fill="none" stroke="currentColor" strokeWidth={3}>
				<path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
			</svg>
		</div>
	)
}

function StepRow({ step }: { step: CompletionStep }) {
	const { t } = useTranslation('editor')
	const isMuted = step.status === 'pending'

	return (
		<div className="flex flex-row items-start gap-3">
			<div className="mt-0.5 shrink-0">
				<StepIndicator status={step.status} />
			</div>
			<div className="flex flex-col">
				<span className={`font-medium ${isMuted ? 'text-grey-400' : 'text-grey-900'}`}>
					{t(`completionProgress.${step.id}.label`)}
				</span>
				<span className="text-sm text-grey-500">
					{step.status === 'skipped'
						? t('completionProgress.authorise.skipped')
						: t(`completionProgress.${step.id}.description`)}
				</span>
				<AnimatePresence>
					{step.messages && step.messages.length > 0 && (
						<motion.ul
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: 'auto' }}
							exit={{ opacity: 0, height: 0 }}
							className="mt-1 list-disc pl-4 text-sm text-red-600"
						>
							{step.messages.map((message) => <li key={message}>{message}</li>)}
						</motion.ul>
					)}
				</AnimatePresence>
			</div>
		</div>
	)
}

export function CompletionProgress({ steps }: { steps: CompletionStep[] }) {
	return (
		<div className="flex flex-col gap-4 min-w-[320px]">
			{steps.map((step) => <StepRow key={step.id} step={step} />)}
		</div>
	)
}
